import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import dayjs from 'dayjs'
import { Badge, Button } from 'antd'
import {
  BellOutlined,
  CloseOutlined,
  CustomerServiceOutlined,
  LineChartOutlined,
  ReadOutlined,
  StarOutlined,
} from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { Preppy } from './Preppy'
import { useAssistant } from './AssistantProvider'
import { GUIDES } from './mockData'
import { AnalysisTab } from './tabs/AnalysisTab'
import { PopupsTab } from './tabs/PopupsTab'
import { GuidesTab } from './tabs/GuidesTab'
import { SupportTab } from './tabs/SupportTab'
import { FeedbackTab } from './tabs/FeedbackTab'
import type { AssistantTabKey } from './types'

/**
 * The assistant shell: one character, five plainly named capabilities, all of
 * them visible as tabs rather than hidden behind a home screen you have to
 * drill in and out of.
 *
 * The tab strip is a hand-built `role="tablist"` rather than antd's Tabs
 * because five stacked icon-and-label tabs have to fit across 420px, which
 * antd's horizontal tab bar will not do without scrolling. Everything inside a
 * panel is an antd primitive.
 */

export const ASSISTANT_PANEL_WIDTH = 420

type TabDefinition = {
  key: AssistantTabKey
  label: string
  title: string
  Icon: typeof LineChartOutlined
}

const TABS: TabDefinition[] = [
  { key: 'analysis', label: 'Analysis', title: 'Analysis Assistant', Icon: LineChartOutlined },
  { key: 'popups', label: 'Pop-ups', title: 'Pop-ups', Icon: BellOutlined },
  { key: 'guides', label: 'Guides', title: 'Guides', Icon: ReadOutlined },
  { key: 'support', label: 'Support', title: 'Support', Icon: CustomerServiceOutlined },
  { key: 'feedback', label: 'Feedback', title: 'Feedback', Icon: StarOutlined },
]

export function AssistantPanel() {
  const { state, actions } = useAssistant()
  const { open, activeTab, mood, orgName, unreadCount } = state

  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<Element | null>(null)

  /**
   * Remember what had focus when the panel opened, and give it back when the
   * panel closes.
   *
   * Keyed on `open` and nothing else. This used to share an effect with the
   * Escape listener below, which meant depending on `actions` — and `actions`
   * is rebuilt whenever a draft changes, so every keystroke in the support or
   * feedback form ran this cleanup and threw focus back to whatever was focused
   * before the panel opened. One character per click of the mouse.
   *
   * The lesson generalises: an effect that restores focus must depend only on
   * the thing whose change should restore it.
   */
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement
    return () => {
      const target = previouslyFocused.current
      if (target instanceof HTMLElement) target.focus()
    }
  }, [open])

  // Escape closes. A panel you can open with the keyboard but not close with it
  // is worse than no panel.
  const setOpen = actions.setOpen
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, setOpen])

  const walkthroughCount = GUIDES.filter((g) => g.scopes.includes(state.scope)).length

  const subtitle = (() => {
    switch (activeTab) {
      case 'analysis': {
        if (state.hasAnalysis) return `Reading ${orgName || 'this analysis'}`
        const count = state.suggestions.items.length
        if (count === 0) return 'Nothing open'
        const noun = state.scope === 'org-list' ? 'organisation' : 'meeting'
        return `${count} ${noun}${count === 1 ? '' : 's'} to pick from`
      }
      case 'popups':
        return `${unreadCount} unread`
      case 'guides':
        return `${walkthroughCount} walkthrough${walkthroughCount === 1 ? '' : 's'}`
      case 'support':
        // The server's own count, not a filter over the loaded rows — same
        // reasoning as feedback's subtitle below.
        return state.awaitingTicketCount > 0
          ? `${state.activeTicketCount} open · ${state.awaitingTicketCount} waiting on you`
          : `${state.activeTicketCount} open`
      case 'feedback': {
        // From the server, not the in-progress form. The draft used to drive
        // this, which meant the subtitle reset to "Not rated yet" the instant
        // you submitted — the one moment it should have said the opposite.
        const rated = state.feedbackStatus
        if (rated?.hasRated && rated.rating) {
          const when = rated.ratedAt ? dayjs(rated.ratedAt).format('MMM YYYY') : ''
          return when ? `You rated ${rated.rating}/5 · ${when}` : `You rated ${rated.rating}/5`
        }
        return state.feedback.rating ? `${state.feedback.rating}/5 — not sent yet` : 'Not rated yet'
      }
      default:
        return 'Your AdvicePrep assistant'
    }
  })()

  const onTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const delta = event.key === 'ArrowRight' ? 1 : -1
    const nextIndex = (index + delta + TABS.length) % TABS.length
    const nextTab = TABS[nextIndex]
    if (!nextTab) return
    actions.openTab(nextTab.key)
    panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]?.focus()
  }

  return createPortal(
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd>
      <aside
        ref={panelRef}
        role="dialog"
        aria-label="Preppy, your AdvicePrep assistant"
        aria-hidden={!open}
        data-open={open}
        className={`preppy-panel fixed bottom-0 right-0 top-0 z-50 flex flex-col border-l border-gray-200 bg-white shadow-xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: ASSISTANT_PANEL_WIDTH }}
      >
        <div className="feature-gradient-surface flex shrink-0 items-center gap-3 border-b border-gray-200 px-5 py-3">
          <Preppy mood={mood} size={40} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-gray-900">Preppy</div>
            <div className="truncate text-[10.5px] text-gray-500">{subtitle}</div>
          </div>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label="Minimise Preppy"
            onClick={() => actions.setOpen(false)}
            className="text-gray-400 hover:text-gray-700"
          />
        </div>

        <div role="tablist" aria-label="Assistant capabilities" className="flex shrink-0 border-b border-gray-200 bg-gray-50">
          {TABS.map((tab, index) => {
            const selected = tab.key === activeTab
            return (
              <button
                key={tab.key}
                role="tab"
                type="button"
                id={`preppy-tab-${tab.key}`}
                aria-selected={selected}
                aria-controls="preppy-tabpanel"
                tabIndex={selected ? 0 : -1}
                title={tab.title}
                onClick={() => actions.openTab(tab.key)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={`relative flex min-w-0 flex-1 cursor-pointer flex-col items-center gap-1 border-0 border-b-2 px-1 pb-1.5 pt-2 text-[10px] font-bold transition-colors ${
                  selected
                    ? 'border-b-[var(--feature-primary)] bg-white text-[var(--feature-primary-strong)]'
                    : 'border-b-transparent bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                {tab.key === 'popups' && unreadCount > 0 ? (
                  <Badge count={unreadCount} size="small" offset={[6, -2]}>
                    <tab.Icon className="text-[17px]" />
                  </Badge>
                ) : tab.key === 'support' && state.awaitingTicketCount > 0 ? (
                  <Badge
                    count={state.awaitingTicketCount}
                    size="small"
                    offset={[6, -2]}
                    title={`${state.awaitingTicketCount} waiting on you`}
                  >
                    <tab.Icon className="text-[17px]" />
                  </Badge>
                ) : (
                  <tab.Icon className="text-[17px]" />
                )}
                <span className="max-w-full truncate">{tab.label}</span>
              </button>
            )
          })}
        </div>

        <div
          id="preppy-tabpanel"
          role="tabpanel"
          aria-labelledby={`preppy-tab-${activeTab}`}
          className={`min-h-0 flex-1 ${activeTab === 'analysis' ? 'flex flex-col' : 'overflow-y-auto px-4 py-4'}`}
        >
          {activeTab === 'analysis' && <AnalysisTab />}
          {activeTab === 'popups' && <PopupsTab />}
          {activeTab === 'guides' && <GuidesTab />}
          {activeTab === 'support' && <SupportTab />}
          {activeTab === 'feedback' && <FeedbackTab />}
        </div>
      </aside>
    </FeatureThemeProvider>,
    document.body
  )
}
